import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { findDOMNode } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import Immutable from 'immutable';
import globalNavHeight from 'nav-meta/global-nav-height';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H4 from 'UIComponents/elements/headings/H4';
import UIBanner from 'UIComponents/alert/UIBanner';
import UIButton from 'UIComponents/button/UIButton';
import UICardGrid from 'UIComponents/card/UICardGrid';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import { Flow } from 'signup-constants/Flow';
import { SignupUrlGenerator } from 'signup-ui-url-generator/SignupUrlGenerator';
import * as RequestStates from 'ecosystem-marketplace-core/constants/RequestState';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { isFulfilled, isUninitialized } from 'ecosystem-marketplace-core/utils/requestState';
import { getIsPrerenderUserAgent } from 'ecosystem-marketplace-core/utils/userAgent';
/* @ts-expect-error module is untyped */

import { usePrevious } from 'ecosystem-marketplace-core/hooks/usePrevious';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import { useScreenSize } from 'ecosystem-marketplace-core/hooks/useScreenSize';

/* @ts-expect-error module is untyped */
import MainSection from 'ecosystem-marketplace-core/components/layout/MainSection';
/* @ts-expect-error module is untyped */

import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
/* @ts-expect-error module is untyped */

import MediaQuery from 'ecosystem-marketplace-core/components/layout/MediaQuery';
/* @ts-expect-error module is untyped */

import ScrollToResults from 'ecosystem-marketplace-core/components/storefront/ScrollToResults';
import * as ThemeQueryField from '../constants/ThemeQueryField';
import { getComputedThemeQuery, getLocationFromThemeQuery, toFilterTags, toQueryString, toSearchQuery } from '../utils/themeQuery';
import { getOffsetFromPageNumber, getPaginatedPathname } from '../utils/pagination';
import { isQa } from '../utils/env';
import { PublicAppContext } from '../selectors/app';
import { searchThemes } from '../clients/ecosystemClient';
import { ThemeSortDropdown } from '../components/storefront/filters/ThemeSortInput';
import { useStorefrontRouteParams } from '../hooks/useStorefrontRouteParams';
import DesktopFilterFields from '../components/storefront/filters/DesktopFilterFields';
import MobileFilterPanel from '../components/storefront/filters/MobileFilterPanel';
import ResultsHeader from '../components/storefront/resultsHeader/ResultsHeader';
import ResultsFilters from '../components/storefront/resultsFilters/ResultsFilters';
import RouterThemeCard from '../components/storefront/results/RouterThemeCard';
import StorefrontDocumentHead from '../components/storefront/documentHead/StorefrontDocumentHead';
/* @ts-expect-error module is untyped */

import StorefrontUsageTrackerContext from '../context/StorefrontUsageTrackerContext';
/* @ts-expect-error module is untyped */

import StorefrontViewToggle from '../components/storefront/StorefrontViewToggle';
import { DEFAULT_THEME_SORT_VALUE, THEME_QUERY_FILTER_ORDER } from '../records/ThemeQuery';
import ThemeSearchResponse from '../records/ThemeSearchResponse';
/* @ts-expect-error module is untyped */

import UsageViewTracker from '../components/tracking/UsageViewTracker';
const EMPTY_SEARCH_RESPONSE = new ThemeSearchResponse();
const NAV_BAR_HEIGHT_INT = parseInt(globalNavHeight, 10);

const getFullPathnameFromLocation = ({
  pathname,
  search
}) => {
  return `${pathname}${search}`;
};

const getPageviewKey = (pathname, searchTerm) => {
  return `pageview::${pathname}${searchTerm ? `?${searchTerm}` : ''}`;
};

const getFilterViewKey = (pathname, themeQuery) => {
  return `filterView::${pathname}?${toQueryString(themeQuery)}`;
};

const maybeGetI18nComponentText = component => {
  if (!(component instanceof FormattedMessage)) return null;
  const {
    message: componentMessage,
    options: componentOptions
  } = component.props;
  return unescapedText(componentMessage, componentOptions);
};

const ThemesStorefrontContainer = ({
  collectionCategory,
  collectionQuery,
  description,
  title
}) => {
  const history = useHistory();
  const routeParams = useStorefrontRouteParams();
  const location = useLocation();
  const {
    pageNumber: computedPageNumber
  } = routeParams;
  const {
    pathname: currentPathname
  } = location;
  const prevPathname = usePrevious(currentPathname);
  const fullPathname = getFullPathnameFromLocation(location);
  const fullPathnameRef = useRef(fullPathname);
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.SM]: false
    }
  });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const passedDescriptionText = useMemo(() => maybeGetI18nComponentText(description), [description]);
  const passedTitleText = useMemo(() => maybeGetI18nComponentText(title), [title]);
  /**
   * Theme query
   */

  const {
    sortFields: collectionSortField = undefined,
    limit: collectionLimit = undefined
  } = collectionQuery ? collectionQuery.toJS() : {};
  const themeQueryUpdatedRef = useRef(false);
  const [themeQuery, setThemeQuery] = useState(getComputedThemeQuery(routeParams, location, collectionSortField, collectionLimit));
  const {
    [ThemeQueryField.LIMIT]: limit,
    [ThemeQueryField.SORT_FIELDS]: sortFieldsValue,
    [ThemeQueryField.OFFSET]: computedOffset,
    [ThemeQueryField.SEARCH]: searchTerm
  } = themeQuery;
  const prevThemeQuery = usePrevious(themeQuery);
  const prevSearchTerm = usePrevious(searchTerm);
  const mergedThemeQuery = useMemo(() => {
    if (!collectionQuery) {
      return themeQuery;
    }

    return themeQuery.mergeWith((themeQueryValue, collectionQueryValue, field) => {
      return collectionQuery.hasQueryFieldValue(field) ? collectionQueryValue : themeQueryValue;
    }, collectionQuery);
  }, [collectionQuery, themeQuery]);
  /**
   * Theme results
   */

  const [themeResultsView, setThemeResultsView] = useState(getIsPrerenderUserAgent() ? 'list' : 'grid');
  const [requestState, setRequestState] = useState(RequestStates.UNINITIALIZED);
  const [searchResponse, setSearchResponse] = useState(EMPTY_SEARCH_RESPONSE);
  const {
    items: results,
    total
  } = searchResponse;
  const resultsSize = results.size;
  const computedPageCount = useMemo(() => {
    return Math.ceil(total / limit);
  }, [limit, total]);
  const computedResultsCount = useMemo(() => {
    if (!total) {
      return null;
    }

    const start = computedOffset + 1;
    return {
      start: total ? start : 0,
      end: computedOffset + Math.min(resultsSize, limit),
      total
    };
  }, [computedOffset, limit, resultsSize, total]);
  /**
   * Collection offering IDs
   */

  const [collectionOfferingIds, setCollectionOfferingIds] = useState(null);
  const getCollectionOfferingIds = useCallback(() => {
    if (collectionOfferingIds) return Promise.resolve(collectionOfferingIds);
    return searchThemes([toSearchQuery(collectionQuery)]).then(([{
      items
    }]) => {
      const offeringIds = Immutable.Set(items.map(item => item.offeringId));
      setCollectionOfferingIds(offeringIds);
      return offeringIds;
    });
  }, [collectionOfferingIds, collectionQuery]);
  /**
   * Usage tracking
   */

  const {
    trackInteraction,
    trackBeforeUnloadInteraction,
    trackFilterView,
    trackPageview,
    setDefaultEventProperties
  } = useContext(StorefrontUsageTrackerContext);
  const getDefaultEventProperties = useCallback(() => {
    return Object.assign({}, themeQuery.toUsageEventProperties(), {
      screen: 'storefront',
      type: 'theme',
      category: collectionCategory ? `collection::${collectionCategory}` : undefined,
      pageNumber: computedPageNumber,
      totalCount: total
    });
  }, [collectionCategory, computedPageNumber, themeQuery, total]); // Set default event properties

  useEffect(() => {
    setDefaultEventProperties(getDefaultEventProperties());
  }, [getDefaultEventProperties, setDefaultEventProperties]); // Reset default event properties on unmount

  useEffect(() => () => setDefaultEventProperties(null), [setDefaultEventProperties]); // Cache & update keys to track page/filter views

  const [pageviewKey, setPageviewKey] = useState(getPageviewKey(currentPathname, searchTerm));
  useEffect(() => {
    setPageviewKey(getPageviewKey(currentPathname, searchTerm));
  }, [currentPathname, searchTerm]);
  const [filterViewKey, setFilterViewKey] = useState(null);
  useEffect(() => {
    if (currentPathname === prevPathname && searchTerm === prevSearchTerm && !themeQuery.equals(prevThemeQuery)) {
      setFilterViewKey(getFilterViewKey(currentPathname, themeQuery));
    }
  }, [currentPathname, prevPathname, prevSearchTerm, prevThemeQuery, searchTerm, themeQuery]);
  /**
   * Callback handlers
   */

  const handleThemeQueryChange = useCallback(newThemeQuery => {
    themeQueryUpdatedRef.current = true;
    setThemeQuery(newThemeQuery);
  }, []);
  const handlePageChange = useCallback(({
    target: {
      value
    }
  }) => {
    const newComputedOffset = getOffsetFromPageNumber(value, limit);
    handleThemeQueryChange(themeQuery.set(ThemeQueryField.OFFSET, newComputedOffset));
  }, [handleThemeQueryChange, limit, themeQuery]);
  const handleFilterUpdate = useCallback((field, value) => {
    /* Convert arrays back to Immutable */
    const newValue = Array.isArray(value) ? Immutable.Set(value) : value;
    handleThemeQueryChange(previousThemeQuery => previousThemeQuery.set(field, newValue).delete(ThemeQueryField.OFFSET));
    let eventValue = value;
    if (value && value.constructor.name === 'Object') eventValue = Object.entries(value).map(([k, v]) => `${k}=${v}`);
    trackInteraction('change filter', {
      filter: field,
      value: eventValue
    });
  }, [handleThemeQueryChange, trackInteraction]);
  const handleApplyFilters = useCallback(filters => {
    setIsFilterPanelOpen(false);
    const nextThemeQuery = themeQuery.withMutations(mutableThemeQuery => {
      for (const _field of Object.keys(filters)) {
        const field = _field;
        const value = filters[field];

        if (Array.isArray(value)) {
          /* Convert arrays back to Immutable */
          mutableThemeQuery.set(field, Immutable.Set(value));
        } else {
          mutableThemeQuery.set(field, value);
        }
      }

      mutableThemeQuery.delete(ThemeQueryField.OFFSET);
    });
    handleThemeQueryChange(nextThemeQuery);
  }, [themeQuery, handleThemeQueryChange, setIsFilterPanelOpen]);
  const handleClearFilters = useCallback(() => {
    trackInteraction('clear all filters');
    handleThemeQueryChange(themeQuery.withMutations(mutableThemeQuery => {
      THEME_QUERY_FILTER_ORDER.forEach(field => mutableThemeQuery.delete(field));
      mutableThemeQuery.delete(ThemeQueryField.OFFSET);
    }));
  }, [handleThemeQueryChange, themeQuery, trackInteraction]);
  const handleClearFilterValue = useCallback((field, value) => {
    trackInteraction('close filter tag', {
      filter: field,
      value
    });
    const queryValue = themeQuery.get(field);
    const newValue = Immutable.Set.isSet(queryValue) ? queryValue.delete(value).toJS() : null;
    handleFilterUpdate(field, newValue);
  }, [themeQuery, handleFilterUpdate, trackInteraction]);
  const handleSortChange = useCallback(value => {
    handleFilterUpdate(ThemeQueryField.SORT_FIELDS, sortFieldsValue.clear().add(value).toJS());
    trackInteraction('change sort', {
      sortField: value
    });
  }, [handleFilterUpdate, sortFieldsValue, trackInteraction]);
  /**
   * Results scroller
   */

  const resultsRef = useRef(null);
  const [scrollToResultsOpacity, setScrollToResultsOpacity] = useState(0);
  const [scrollToResultsFixed, setScrollToResultsFixed] = useState(true);
  const detectResultsVisible = useCallback(() => {
    const resultsEl = findDOMNode(resultsRef.current);
    if (!(resultsEl instanceof Element)) return;
    if (!resultsEl.parentElement) return;
    setScrollToResultsFixed(resultsEl.parentElement.getBoundingClientRect().bottom > window.innerHeight);
    const rect = resultsEl.getBoundingClientRect();
    setScrollToResultsOpacity((NAV_BAR_HEIGHT_INT - rect.bottom) / 200);
  }, [resultsRef]);
  useEffect(() => {
    window.addEventListener('scroll', detectResultsVisible);
    return () => {
      window.removeEventListener('scroll', detectResultsVisible);
    };
  }, [detectResultsVisible]);
  useEffect(() => detectResultsVisible());
  useEffect(() => {
    if (requestState === RequestStates.SUCCEEDED && computedPageCount > 0 && computedPageNumber > computedPageCount) {
      history.replace(getPaginatedPathname(currentPathname, 1));
    }
  }, [computedPageCount, computedPageNumber, history, currentPathname, requestState]);
  useEffect(() => {
    if (themeQueryUpdatedRef.current) {
      setSearchResponse(EMPTY_SEARCH_RESPONSE);
      setRequestState(RequestStates.UNINITIALIZED);
      const nextLocation = getLocationFromThemeQuery(themeQuery, location, !!collectionCategory);
      history.push(nextLocation);
      fullPathnameRef.current = getFullPathnameFromLocation(nextLocation);
      themeQueryUpdatedRef.current = false;
    }
  }, [collectionCategory, history, location, themeQuery]);
  useEffect(() => {
    if (fullPathname !== fullPathnameRef.current) {
      setSearchResponse(EMPTY_SEARCH_RESPONSE);
      setRequestState(RequestStates.UNINITIALIZED);
      setThemeQuery(getComputedThemeQuery(routeParams, location, collectionSortField, collectionLimit));
      fullPathnameRef.current = getFullPathnameFromLocation(location);
    }
  }, [fullPathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (collectionQuery) {
      setCollectionOfferingIds(null);
    }
  }, [collectionQuery]);
  useEffect(() => {
    if (isUninitialized(requestState)) {
      setRequestState(RequestStates.PENDING);
      let request;

      if (collectionQuery) {
        request = getCollectionOfferingIds().then(offeringIds => {
          const searchQuery = toSearchQuery(themeQuery.set(ThemeQueryField.OFFERING_IDS, offeringIds));
          return searchThemes([searchQuery]);
        });
      } else {
        const searchQuery = toSearchQuery(themeQuery);
        request = searchThemes([searchQuery]);
      }

      request.then(([resp]) => {
        setSearchResponse(new ThemeSearchResponse(resp));
        setRequestState(RequestStates.SUCCEEDED);
      }).catch(error => {
        console.error('error fetching themes\n', error);
        setSearchResponse(EMPTY_SEARCH_RESPONSE);
        setRequestState(RequestStates.FAILED);
      });
    }
  }, [collectionQuery, getCollectionOfferingIds, requestState, themeQuery]);
  const renderedSearchResults = useMemo(() => {
    if (!results.size) {
      return /*#__PURE__*/_jsx(UIFlex, {
        direction: "column",
        children: searchTerm ? /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(H4, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.storefront.header.search.noResults",
              options: {
                searchTerm
              }
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.storefront.header.search.noSearchResultsSubtext"
          })]
        }) : /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(H4, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.storefront.header.noResults"
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.storefront.header.search.noFilterResultsSubtext"
          })]
        })
      });
    }

    return results.map((result, index) => /*#__PURE__*/_jsx(RouterThemeCard, {
      onClick: () => {
        trackBeforeUnloadInteraction('click result item', {
          value: result.slug,
          itemIndex: index,
          totalResults: resultsSize
        });
      },
      result: result.toJS(),
      view: themeResultsView
    }, `result-${index}`));
  }, [results, resultsSize, searchTerm, themeResultsView, trackBeforeUnloadInteraction]);
  const renderedScrollToResults = useMemo(() => {
    const resultsEl = findDOMNode(resultsRef.current);
    if (!(resultsEl instanceof HTMLElement) || results.size === 0) return null;
    return /*#__PURE__*/_jsx(ScrollToResults, {
      containerWidth: resultsEl.offsetWidth,
      opacity: scrollToResultsOpacity,
      fixed: scrollToResultsFixed
    });
  }, [results, scrollToResultsOpacity, scrollToResultsFixed]);
  const renderedViewToggle = useMemo(() => /*#__PURE__*/_jsx(StorefrontViewToggle, {
    activeView: themeResultsView,
    onChange: setThemeResultsView
  }), [themeResultsView, setThemeResultsView]);
  const screenSize = useScreenSize();
  const isXSScreen = screenSize === ScreenSizes.XS;
  const gridColumns = isXSScreen ? 1 : 2;
  const columns = useMemo(() => themeResultsView === 'list' ? 1 : gridColumns, [themeResultsView, gridColumns]);
  const themeFilters = useMemo(() => mergedThemeQuery.toJS(), [mergedThemeQuery]);
  const isFilterDisabled = useCallback(field => {
    return collectionQuery ? collectionQuery.hasQueryFieldValue(field) : false;
  }, [collectionQuery]);
  const signupUrl = useMemo(() => new SignupUrlGenerator(Flow.CmsFree, {
    query: {
      intent: 'marketplaceTheme'
    },
    env: isQa() ? 'qa' : 'prod'
  }).getUrl(), []);
  return /*#__PURE__*/_jsxs(NavMarker, {
    active: requestState === RequestStates.SUCCEEDED,
    name: "THEMES_LOAD",
    children: [/*#__PURE__*/_jsx(StorefrontDocumentHead, {
      description: passedDescriptionText,
      pageCount: computedPageCount,
      requestState: requestState,
      routeParams: routeParams,
      searchTerm: searchTerm,
      title: passedTitleText,
      total: total
    }), /*#__PURE__*/_jsx(MainSection, {
      responsive: true,
      children: /*#__PURE__*/_jsx(MainSectionInner, {
        children: /*#__PURE__*/_jsxs(UIGrid, {
          children: [/*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: ScreenSizes.LG,
            children: /*#__PURE__*/_jsx(UIGridItem, {
              size: {
                lg: 3
              },
              children: /*#__PURE__*/_jsx(DesktopFilterFields, {
                filters: themeFilters,
                isFilterDisabled: isFilterDisabled,
                onFilterChange: handleFilterUpdate
              })
            })
          }), /*#__PURE__*/_jsxs(UIGridItem, {
            size: {
              xs: 12,
              lg: 9
            },
            children: [/*#__PURE__*/_jsx(PublicAppContext, {
              children: /*#__PURE__*/_jsx(UIBanner, {
                titleText: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "marketplace-assets-ui.storefront.signUpBanner.title"
                }),
                children: /*#__PURE__*/_jsxs(UIFlex, {
                  align: "center",
                  direction: isMobileView ? 'column' : 'row',
                  justify: "start",
                  children: [/*#__PURE__*/_jsx(FormattedMessage, {
                    message: "marketplace-assets-ui.storefront.signUpBanner.message"
                  }), /*#__PURE__*/_jsx(UIButton, {
                    className: isMobileView ? '' : 'm-left-2',
                    href: signupUrl,
                    onClick: () => trackBeforeUnloadInteraction('click cms sign up link'),
                    size: "extra-small",
                    style: {
                      display: 'block',
                      minWidth: '70px'
                    },
                    use: "primary-white",
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "marketplace-assets-ui.storefront.signUpBanner.signUp"
                    })
                  })]
                })
              })
            }), /*#__PURE__*/_jsx(ResultsHeader, {
              className: "m-top-6",
              description: description,
              renderedResultsFilters: /*#__PURE__*/_jsx(ResultsFilters, {
                filters: toFilterTags(themeQuery),
                onClearFilter: handleClearFilterValue,
                onClearAllFilters: handleClearFilters,
                onOpenFilterPanel: () => setIsFilterPanelOpen(true)
              }),
              renderedSortDropdown: /*#__PURE__*/_jsx(MediaQuery, {
                minScreenSize: ScreenSizes.LG,
                children: /*#__PURE__*/_jsx(ThemeSortDropdown, {
                  onSelect: value => handleSortChange(value),
                  value: sortFieldsValue.first() || DEFAULT_THEME_SORT_VALUE
                })
              }),
              resultsCount: computedResultsCount,
              routeParams: routeParams,
              searchTerm: searchTerm || undefined,
              title: title,
              viewToggle: renderedViewToggle
            }), requestState === RequestStates.PENDING && /*#__PURE__*/_jsx(UILoadingSpinner, {
              layout: "centered",
              minHeight: 150
            }), requestState === RequestStates.SUCCEEDED && /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsxs("div", {
                ref: resultsRef,
                "data-test-id": "storefront-results-container",
                children: [/*#__PURE__*/_jsx(UICardGrid, {
                  gutterSize: 16,
                  columns: columns,
                  children: renderedSearchResults
                }), computedPageCount > 1 && /*#__PURE__*/_jsx(UIPaginator, {
                  className: "m-top-8",
                  onPageChange: handlePageChange,
                  page: computedPageNumber,
                  pageCount: computedPageCount
                })]
              }), renderedScrollToResults]
            })]
          })]
        })
      })
    }), /*#__PURE__*/_jsx(UsageViewTracker, {
      active: isFulfilled(requestState),
      onTrackView: trackPageview
    }, pageviewKey), filterViewKey && /*#__PURE__*/_jsx(UsageViewTracker, {
      active: isFulfilled(requestState),
      onTrackView: trackFilterView
    }, filterViewKey), /*#__PURE__*/_jsx(MobileFilterPanel, {
      filters: themeFilters,
      isFilterDisabled: isFilterDisabled,
      onApplyFilters: handleApplyFilters,
      onClearFilters: handleClearFilters,
      onClose: () => setIsFilterPanelOpen(false),
      open: isFilterPanelOpen
    })]
  });
};

export default ThemesStorefrontContainer;